import React from 'react';
import classnames from 'classnames';
import { Link, graphql } from 'gatsby';
import animateScrollTo from 'animated-scroll-to';
import { FiPrinter } from 'react-icons/fi';

import ExcessMortality from '../graphs/ExcessMortality';
import PooledByAgeGroup from '../graphs/PooledByAgeGroup';
import ZByCountry from '../graphs/ZByCountry';
import ZByCountryMap from '../graphs/ZByCountryMap';

import Layout from '../components/Layout';
import Meta from '../components/Meta';

import { win, getDocumentProp } from '../utils/dom';
import { getGraphsData } from '../utils/graphs';
import { arrayToSentence } from '../utils/text';
import { usePrinting } from '../utils/hooks';
import { countryToCode } from '../utils/data';

import grid from '../styles/grid.module.css';
import css from './graphs-and-maps.module.css';
import graphsJson from '../graphs/graphs.json';

const {
	ages,
	countries,
	reportYear,
	reportWeek,
	reportDay,
	pooledByAgeGroup,
	cumulatedExcessMortality,
	zByCountryMap,
	zByCountry,
} = getGraphsData(graphsJson);

const submenu = [
	{ id: 'pooled-by-age-group', label: ['Pooled number of deaths', 'by age group'] },
	{ id: 'excess-mortality', label: 'Excess mortality' },
	{ id: 'map-of-z-scores', label: 'Map of z-scores' },
	{ id: 'z-scores-by-country', label: 'Z-scores by country' },
];

const GraphsAndData = ({ navigate, location, data }) => {
	const { excerpt, html } = data.intro;
	const [printing, triggerPrint] = usePrinting();

	const allCountries = Object.keys(countryToCode);

	const scrollToAnchor = async (e, id) => {
		if (!win) return;
		e.preventDefault();
		await animateScrollTo(document.querySelector(`#${id}`), { maxDuration: 1000 });
		navigate(`${location.pathname}#${id}`, {}, true);
	};

	const missingCountries = allCountries.filter((c) => !countries.includes(c));

	let missingParagraph = null;
	if (missingCountries.length > 0) {
		missingParagraph = <p>{arrayToSentence(missingCountries)} did not contribute to this week's data.</p>;
	}

	const label = (
		<>
			<p className={css.participatingCountries}>
				These graphs were generated in week {reportYear}-{reportWeek} with data from {countries.length}{' '}
				participating countries: {arrayToSentence(countries)}.
			</p>
			{missingParagraph}
			<p>Ukraine, Germany (Berlin) and Germany (Hesse) were not included in the pooled data.</p>
		</>
	);

	return (
		<Layout title="Graphs and maps">
			<Meta title="Graphs and maps from EUROMOMO" description={excerpt} />
			<article className={classnames(grid.root, css.root)}>
				<header className={classnames(css.header, grid.root)}>
					<div className={classnames(css.title, grid.column6)}>
						<h1>Graphs and maps</h1>
						<p className={css.date}>
							Last updated on{' '}
							<time>
								week {reportWeek}, {reportYear}
							</time>
							<span className={css.print}>
								<a href="#" onClick={triggerPrint}>
									<FiPrinter />
									<span>Print</span>
								</a>
							</span>
						</p>
						<div className={css.intro} dangerouslySetInnerHTML={{ __html: html }} />
						{label}
					</div>
					<div className={classnames(css.menu, grid.column2)}>
						<h3 className={css.title}>Graphs</h3>
						<ul>
							{submenu.map((item) => (
								<li key={item.id}>
									<Link
										to={`/graphs-and-maps/#${item.id}`}
										onClick={(e) => scrollToAnchor(e, item.id)}>
										{[].concat(item.label).map((l, i) => (
											<span key={i}>{l}</span>
										))}
									</Link>
								</li>
							))}
						</ul>
					</div>
				</header>
				<section className={grid.root}>
					<PooledByAgeGroup data={pooledByAgeGroup} ages={ages} printing={printing} />
					<ExcessMortality data={cumulatedExcessMortality} ages={ages} printing={printing} />
					<ZByCountryMap
						data={zByCountryMap}
						printing={printing}
						reportYear={reportYear}
						reportWeek={reportWeek}
					/>
					<ZByCountry data={zByCountry} ages={ages} printing={printing} />
				</section>
			</article>
		</Layout>
	);
};

export default GraphsAndData;

export const pageQuery = graphql`
	query ($path: String!) {
		intro: markdownRemark(fields: { slug: { eq: $path } }) {
			frontmatter {
				title
				date
			}
			html
			excerpt
		}
	}
`;
